import { useState } from "react";
import { Button } from "@/design-system/primitives";
import {
  useWithdrawDoseChangeHold,
  type DoseChangeHold,
  type WithdrawReason,
} from "@/lib/api/doseChangeHolds";

export const WITHDRAW_REASON_LABEL: Record<WithdrawReason, string> = {
  duplicate: "Duplicate of another request",
  no_change_available: "No dose change available",
  not_a_dose_request: "Not a dose request",
};

/** Clear a duplicate dose-change request by folding it into another request for
 * the same treatment that is still open. The hold stays, so clearing never
 * releases one. */
export function ClearDoseChangeRequest({
  hold,
  others,
}: {
  hold: DoseChangeHold;
  /** Other requests on file for the same patient. */
  others: DoseChangeHold[];
}) {
  const withdraw = useWithdrawDoseChangeHold(hold.patient_id);
  const targets = others.filter(
    (o) =>
      o.id !== hold.id &&
      o.product_id === hold.product_id &&
      o.status === "open",
  );
  const [open, setOpen] = useState(false);
  const [duplicateOf, setDuplicateOf] = useState(targets[0]?.id ?? "");
  const [note, setNote] = useState("");
  if (!targets.length) return null;
  const ready = !withdraw.isPending && targets.some((t) => t.id === duplicateOf);

  if (!open)
    return (
      <Button
        type="button"
        variant="secondary"
        className="self-start"
        onClick={() => setOpen(true)}
      >
        Clear as duplicate
      </Button>
    );
  return (
    <form
      className="flex flex-col gap-10"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        withdraw.mutate(
          {
            requestId: hold.id,
            body: {
              reason: "duplicate",
              note: note.trim(),
              duplicate_of: duplicateOf,
              expected_resolution_version: hold.resolution_version,
            },
          },
          { onSuccess: () => setOpen(false) },
        );
      }}
    >
      <p className="text-14">
        Clears this request as a repeat of another open request for the same
        treatment. That request keeps the hold and shows this ask too, so the
        prescriber decides both at once.
      </p>
      <label className="text-14 flex flex-col gap-5">
        Duplicate of
        <select
          value={duplicateOf}
          onChange={(event) => setDuplicateOf(event.target.value)}
          className="bg-raised border border-line p-10"
        >
          {targets.map((t) => (
            <option key={t.id} value={t.id}>
              {new Date(t.created_at).toLocaleString()} ·{" "}
              {t.source_channel.replaceAll("_", " ")} · {t.status}
            </option>
          ))}
        </select>
      </label>
      <label className="text-14 flex flex-col gap-5">
        Note (optional)
        <textarea
          maxLength={2000}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className="bg-raised border border-line p-10"
        />
      </label>
      <div className="flex gap-10">
        <Button type="submit" disabled={!ready}>
          Clear as duplicate
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={withdraw.isPending}
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
      </div>
      {withdraw.isError && (
        <p role="alert" className="text-14 text-danger">
          {withdraw.error instanceof Error
            ? withdraw.error.message
            : "The request could not be cleared. Refresh and retry."}
        </p>
      )}
    </form>
  );
}
